import { useEffect, useState, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, GeoJSON, CircleMarker, Polyline, Popup, Tooltip, useMap, useMapEvents } from 'react-leaflet';
// georaster + georaster-layer-for-leaflet are loaded dynamically after Leaflet is available.
// (UMD scripts bypass webpack ESM conflicts with worker-loader and georaster-stack.)
// They set window.parseGeoraster and window.GeoRasterLayer respectively.
let _georasterLibsLoaded = false;
function loadGeorasterLibs() {
  if (_georasterLibsLoaded) return Promise.resolve();
  if (window.parseGeoraster && window.GeoRasterLayer) { _georasterLibsLoaded = true; return Promise.resolve(); }
  // Expose Leaflet globally so georaster-layer-for-leaflet can find it
  if (!window.L) window.L = L;
  return new Promise((resolve, reject) => {
    const s1 = document.createElement('script');
    s1.src = '/vendor/georaster.browser.bundle.min.js';
    s1.onload = () => {
      const s2 = document.createElement('script');
      s2.src = '/vendor/georaster-layer-for-leaflet.min.js';
      s2.onload = () => { _georasterLibsLoaded = true; resolve(); };
      s2.onerror = () => reject(new Error('Failed to load georaster-layer-for-leaflet'));
      document.head.appendChild(s2);
    };
    s1.onerror = () => reject(new Error('Failed to load georaster'));
    document.head.appendChild(s1);
  });
}
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControlLabel, Checkbox, IconButton, MenuItem,
  Paper, Select, Slider, Stack, TextField, ToggleButton, ToggleButtonGroup,
  Typography,
} from '@mui/material';
import Iconify from '../iconify';

// ---------------------------------------------------------------------------
// Basemaps
// ---------------------------------------------------------------------------
const SATELLITE_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const SATELLITE_ATTR = 'Tiles &copy; Esri &mdash; Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, GIS User Community';

const BASEMAPS = {
  osm: {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  },
  satellite: { url: SATELLITE_URL, attribution: SATELLITE_ATTR },
};

const WAYBACK_CONFIG_URL = 'https://s3-us-west-2.amazonaws.com/config.maptiles.arcgis.com/waybackconfig.json';

// Fetch Wayback snapshots — one per year (latest snapshot in that year)
async function fetchWaybackSnapshots() {
  const res = await fetch(WAYBACK_CONFIG_URL);
  const data = await res.json();
  const byYear = {};
  for (const [releaseNum, item] of Object.entries(data)) {
    const m = (item.itemTitle || '').match(/Wayback (\d{4}-\d{2}-\d{2})/);
    if (!m) continue;
    const year = parseInt(m[1].slice(0, 4), 10);
    const rn = parseInt(releaseNum, 10);
    if (!byYear[year] || rn > byYear[year].releaseNum) {
      // Replace {level}/{row}/{col} with Leaflet placeholders
      const tileUrl = (item.itemURL || '')
        .replace('{level}', '{z}')
        .replace('{row}', '{y}')
        .replace('{col}', '{x}');
      byYear[year] = { year, date: m[1], releaseNum: rn, tileUrl };
    }
  }
  return Object.values(byYear).sort((a, b) => a.year - b.year);
}

// ---------------------------------------------------------------------------
// Annotation types
// ---------------------------------------------------------------------------
export const ANNOTATION_TYPES = [
  { key: 'sensor',          label: 'Sensor',          color: '#2196f3' },
  { key: 'water_level',     label: 'Water Level',     color: '#00bcd4' },
  { key: 'overflow',        label: 'Water Overflow',  color: '#ff9800' },
  { key: 'boundary_marker', label: 'Boundary Marker', color: '#4caf50' },
  { key: 'observation',     label: 'Observation',     color: '#9c27b0' },
  { key: 'other',           label: 'Other',           color: '#607d8b' },
];
function annotationColor(type) {
  return ANNOTATION_TYPES.find(t => t.key === type)?.color || '#607d8b';
}

// ---------------------------------------------------------------------------
// Strata palette
// ---------------------------------------------------------------------------
const STRATA_PALETTE = [
  '#e41a1c', '#377eb8', '#4daf4a', '#ff7f00', '#984ea3',
  '#a65628', '#f781bf', '#aaaaaa', '#d4b400', '#00bcd4',
  '#8bc34a', '#ff5722', '#795548', '#607d8b', '#e91e63',
  '#9c27b0', '#3f51b5', '#2196f3', '#009688', '#cddc39',
];
// Strata arrive either numbered (legacy artifacts) or named after their KML
// folder ("Stratum A (5)", "QU-A (3)"). Named strata get a stable palette slot
// in first-seen order — Number() on a name is NaN, which indexes the palette
// to undefined and Leaflet then falls back to the white border colour.
const namedStrataSlots = new Map();
function strataColor(stratum) {
  if (stratum == null) return '#888';
  const n = Number(stratum);
  if (Number.isFinite(n)) return STRATA_PALETTE[(n - 1) % STRATA_PALETTE.length];
  const key = String(stratum);
  if (!namedStrataSlots.has(key)) namedStrataSlots.set(key, namedStrataSlots.size);
  return STRATA_PALETTE[namedStrataSlots.get(key) % STRATA_PALETTE.length];
}

const FR_STATUS_COLOR = { visited: '#4caf50', pending: '#ff9800', skipped: '#ffc107', added: '#2196f3' };
function frStatusColor(status) { return FR_STATUS_COLOR[status] || '#9e9e9e'; }

// ---------------------------------------------------------------------------
// Parse artifact payload_json from API response
// ---------------------------------------------------------------------------
function parseArtifactFeatures(data) {
  const payload = data?.payload_json
    ? (typeof data.payload_json === 'string' ? JSON.parse(data.payload_json) : data.payload_json)
    : data;
  return payload?.features || (Array.isArray(payload) ? payload : []);
}

// ---------------------------------------------------------------------------
// Auth header
// ---------------------------------------------------------------------------
function bearerHeaders() {
  const tkn = localStorage.getItem('tkn');
  return { 'Content-Type': 'application/json', ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) };
}

// ---------------------------------------------------------------------------
// Leaflet helpers
// ---------------------------------------------------------------------------
// Fly to a specific point (evidence deep-link) — overrides the boundary fit.
function FlyToPoint({ pt }) {
  const map = useMap();
  useEffect(() => {
    if (pt) map.setView([pt.lat, pt.lng], 19);
  }, [pt, map]);
  return null;
}

function BoundsFitter({ boundary }) {
  const map = useMap();
  useEffect(() => {
    if (!boundary) return;
    try {
      const bounds = L.geoJSON(boundary).getBounds();
      if (bounds.isValid()) map.fitBounds(bounds, { padding: [48, 48] });
    } catch (_) {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundary]);
  return null;
}

function MapClickHandler({ placing, onMapClick }) {
  useMapEvents({ click(e) { if (placing) onMapClick(e.latlng); } });
  return null;
}

// Build a viridis-like color ramp for scientific data
function buildColorFn(georaster) {
  const mins = georaster.mins || [];
  const maxs = georaster.maxs || [];
  // Guard against NaN/null/undefined stats (common for COG files without embedded stats)
  const rawMin = mins[0];
  const rawMax = maxs[0];
  const min = (typeof rawMin === 'number' && isFinite(rawMin)) ? rawMin : 0;
  const max = (typeof rawMax === 'number' && isFinite(rawMax) && rawMax !== rawMin) ? rawMax : min + 1;
  const range = max - min;

  // Simple viridis-ish ramp: dark purple → blue → green → yellow
  const ramp = [
    [68, 1, 84], [72, 36, 117], [65, 68, 135], [53, 95, 141],
    [42, 120, 142], [33, 145, 140], [34, 168, 132], [68, 191, 112],
    [122, 209, 81], [189, 223, 38], [253, 231, 37],
  ];

  return (values) => {
    const v = values[0]; // use first band
    if (v == null || !isFinite(v) || v === georaster.noDataValue) return null;
    const t = Math.max(0, Math.min(1, (v - min) / range));
    const idx = t * (ramp.length - 1);
    const lo = Math.floor(idx);
    const hi = Math.min(lo + 1, ramp.length - 1);
    const f = idx - lo;
    return `rgba(${Math.round(ramp[lo][0] + f * (ramp[hi][0] - ramp[lo][0]))},${Math.round(ramp[lo][1] + f * (ramp[hi][1] - ramp[lo][1]))},${Math.round(ramp[lo][2] + f * (ramp[hi][2] - ramp[lo][2]))},0.8)`;
  };
}

// Renders a COG/GeoTIFF via georaster-layer-for-leaflet
function GeoRasterLayer({ url, onLoad }) {
  const map = useMap();
  useEffect(() => {
    let layer;
    let cancelled = false;
    (async () => {
      try {
        await loadGeorasterLibs();
        const parseGeorasterFn = window.parseGeoraster;
        const GeoRasterLayerClass = window.GeoRasterLayer;
        if (typeof GeoRasterLayerClass !== 'function' || typeof parseGeorasterFn !== 'function') {
          console.error('[GeoRasterLayer] georaster libs not loaded');
          return;
        }
        const response = await fetch(url);
        if (cancelled) return;
        if (!response.ok) {
          const msg = await response.text().catch(() => response.statusText);
          throw new Error(`HTTP ${response.status}: ${msg}`);
        }
        const arrayBuffer = await response.arrayBuffer();
        if (cancelled) return;
        const georaster = await parseGeorasterFn(arrayBuffer);
        if (cancelled) return;

        console.log('[GeoRasterLayer] parsed', {
          bands: georaster.numberOfRasters,
          width: georaster.width, height: georaster.height,
          mins: georaster.mins?.slice(0, 3), maxs: georaster.maxs?.slice(0, 3),
          noDataValue: georaster.noDataValue,
          xmin: georaster.xmin, ymin: georaster.ymin, xmax: georaster.xmax, ymax: georaster.ymax,
          projection: georaster.projection,
        });

        layer = new GeoRasterLayerClass({
          georaster,
          opacity: 0.75,
          resolution: 256,
          pixelValuesToColorFn: buildColorFn(georaster),
        });
        layer.addTo(map);

        // Fit map to the raster bounds
        try {
          const bounds = layer.getBounds?.() || L.latLngBounds(
            [georaster.ymin, georaster.xmin],
            [georaster.ymax, georaster.xmax],
          );
          if (bounds?.isValid()) map.fitBounds(bounds, { padding: [48, 48] });
        } catch (boundsErr) {
          console.warn('[GeoRasterLayer] bounds fit failed:', boundsErr.message);
        }
      } catch (e) {
        console.error('[GeoRasterLayer] failed to load raster:', e.message, e);
      } finally {
        if (!cancelled) onLoad?.();
      }
    })();
    return () => {
      cancelled = true;
      if (layer) { try { map.removeLayer(layer); } catch (_) {} }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);
  return null;
}

// ---------------------------------------------------------------------------
// Annotation dialog
// ---------------------------------------------------------------------------
const BLANK_FORM = { type: 'observation', label: '', notes: '' };

function AnnotationDialog({ open, initial, onSave, onClose }) {
  const [form, setForm] = useState(BLANK_FORM);
  const isEdit = Boolean(initial?.id);
  useEffect(() => {
    if (open) setForm(initial ? { type: initial.type, label: initial.label, notes: initial.notes || '' } : BLANK_FORM);
  }, [open, initial]);
  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Annotation' : 'New Annotation'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Type</Typography>
            <Select size="small" fullWidth value={form.type} onChange={set('type')}>
              {ANNOTATION_TYPES.map(t => (
                <MenuItem key={t.key} value={t.key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: t.color, flexShrink: 0 }} />
                    {t.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </Box>
          <TextField label="Label" size="small" fullWidth value={form.label} onChange={set('label')}
            placeholder="e.g. WLC-01, Sensor North Gate…" />
          <TextField label="Notes" size="small" fullWidth multiline minRows={2} value={form.notes} onChange={set('notes')}
            placeholder="Optional description…" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(form)} disabled={!form.label.trim()}>
          {isEdit ? 'Save Changes' : 'Add to Map'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Sidebar helpers
// ---------------------------------------------------------------------------
const PANEL_WIDTH = 248;

function LayerDot({ color }) {
  return (
    <Box component="span" sx={{
      display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
      bgcolor: color, border: '1.5px solid rgba(0,0,0,0.2)', mr: 0.75, flexShrink: 0,
    }} />
  );
}
// Photo tree for the sidebar: FOLDER (gate, or "Initial soil sampling") → LEAF
// (task, or the measurement unit for pre-task-era captures). Mirrors the task list
// the project manages in the checklist and shows in the field app (Yuval, 19/09).
// Gate display names mirror the checklist sheet — cosmetic only, counts and grouping
// derive from the data.
// Ref-prefix → gate, matching the checklist rebuild of 22/09 (refs MOW*/B*/DISC*/
// SEED*/F*/E1 — the prefix is NOT the gate letter). Codex finding: single-char
// inference put MOW under a nonexistent "Gate M". This map is the interim shared
// taxonomy; the review-flow build promotes it to a versioned server-side mapping.
const GATE_LABELS = {
  A: 'A · Site known and recorded',
  MOW: 'B · Mowing',
  B: 'C · Baseline measured',
  DISC: 'D · Discing',
  SEED: 'E · Seeding',
  F: 'F · Infrastructure',
  E: 'G · Start',
  C: 'Ground work (retired refs)',
};
const GATE_ORDER = ['A', 'MOW', 'B', 'DISC', 'SEED', 'F', 'E', 'C'];
function refPrefix(taskId) {
  const m = /^[A-Z]+/.exec(String(taskId || ''));
  return m ? m[0] : '';
}
function photoLeafOf(e) {
  if (!e || !e.task_id) {
    // Pre-task-era capture (the spring campaign): group by the unit it measured —
    // the sampling point id when it has one, one shared bucket when it doesn't.
    const unit = e && e.sampling_point ? String(e.sampling_point) : null;
    return {
      folder: '_initial', folderLabel: 'Initial soil sampling', folderSort: '0',
      key: 'initial:' + (unit || 'other'),
      label: unit || 'No point id',
      sort: unit ? '0' + unit : '1~',
    };
  }
  const gate = refPrefix(e.task_id);
  const order = GATE_ORDER.indexOf(gate);
  return {
    folder: 'gate:' + gate,
    folderLabel: GATE_LABELS[gate] || `Gate ${gate}`,
    folderSort: '1' + String(order < 0 ? 9 : order),
    key: String(e.task_id),
    label: e.task_label ? `${e.task_id} · ${e.task_label}` : String(e.task_id),
    sort: String(e.task_id),
  };
}

// Per-task photo colors (Yuval, 19/09): each task in a gate gets its own marker
// color, mirrored as a dot in the sidebar tree — boundaries red, sensors blue,
// water yellow, etc. Known tasks are pinned so colors never shift; anything else
// (e.g. the initial-sampling units) hashes into the palette deterministically.
const PHOTO_TASK_COLORS = {
  A2: '#e53935',   // parcel boundaries — red
  A3: '#1e88e5',   // weather sensors — blue
  A4: '#fdd835',   // water-level sensor — yellow
  A5: '#8e24aa',   // canal gate — purple
  MOW6: '#43a047', // reed placement — green
  DISC6: '#6d4c41',// discing depth — brown
  B1: '#00897b',   // plot marking — teal
  B2: '#c2185b',   // bulk density — magenta
  B3: '#7cb342',   // composite — light green
};
const PHOTO_PALETTE = ['#ff5722', '#00897b', '#c2185b', '#7cb342', '#5e35b1', '#00acc1',
  '#f06292', '#9e9d24', '#3949ab', '#ef6c00', '#26a69a', '#8d6e63'];
function photoColor(key) {
  if (PHOTO_TASK_COLORS[key]) return PHOTO_TASK_COLORS[key];
  let h = 0;
  const s = String(key || '');
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return PHOTO_PALETTE[h % PHOTO_PALETTE.length];
}

// Polygons had no popup at all — clicking "AB · Sabbia" showed nothing. Bind the
// feature's own name (and stratum when it differs) on every polygon layer.
function bindPolygonPopup(feature, layer) {
  const p = feature?.properties || {};
  const name = p.name || p.label || '';
  const stratum = p.stratum != null ? String(p.stratum) : '';
  const text = name && stratum && name !== stratum ? `${name} — ${stratum}` : (name || stratum);
  if (text) layer.bindPopup(String(text));
}

function LayerRow({ show, onChange, children }) {
  return (
    <FormControlLabel sx={{ m: 0, display: 'flex', alignItems: 'center', minHeight: 28 }}
      control={<Checkbox size="small" checked={show} onChange={e => onChange(e.target.checked)} sx={{ py: 0.25, pr: 0.5 }} />}
      label={<Box sx={{ display: 'flex', alignItems: 'center' }}>{children}</Box>}
    />
  );
}

function metresApart(lat1, lng1, lat2, lng2) {
  const R = 6371000, toRad = Math.PI / 180;
  const dLat = (lat2 - lat1) * toRad, dLng = (lng2 - lng1) * toRad;
  const h = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * toRad) * Math.cos(lat2 * toRad) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

// One marker for all captures of the same task at the same spot. Renders ONE photo
// at a time — a group can hold dozens, and stacking them all makes the popup
// unusable and fetches every image whether it is looked at or not.
function EvidenceGroupMarker({ group }) {
  const [i, setI] = useState(0);
  const items = group.items;
  const n = items.length;
  const idx = Math.min(i, n - 1);
  const e = items[idx];
  const first = items[0];
  // `category` is a bucket ('activity'), not a name. Prefer the task label, then the
  // title, and only then the bucket. Captures made before task_label existed have
  // the full task sentence as their title, so trim it to keep the popup narrow.
  const heading = first.task_label
    ? `${first.task_id ? first.task_id + ' — ' : ''}${first.task_label}`
    : (first.sampling_point
      || (first.title && first.title.length > 42 ? `${first.title.slice(0, 40)}…` : first.title)
      || first.category || 'Evidence');
  const step = (d) => (ev) => { ev.preventDefault(); ev.stopPropagation(); setI((idx + d + n) % n); };

  return (
    <CircleMarker center={[group.lat, group.lng]} radius={n > 1 ? 10 : 7}
      pathOptions={{ color: '#fff', weight: 2, fillColor: photoColor(photoLeafOf(first).key), fillOpacity: 0.95 }}>
      {n > 1 && <Tooltip permanent direction="top" offset={[0, -10]}>{`📷 ${n}`}</Tooltip>}
      <Popup maxWidth={240}>
        <Box sx={{ minWidth: 190 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
            {heading}
          </Typography>
          <img src={`/v1/evidence/${e.id}/image`} alt={heading}
            style={{ width: 200, maxHeight: 150, objectFit: 'cover', borderRadius: 4, display: 'block', marginBottom: 4 }}
            onError={(ev) => { ev.target.style.display = 'none'; }} />
          {n > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Button size="small" onClick={step(-1)} sx={{ minWidth: 28, px: 0.5 }}>‹</Button>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{idx + 1} / {n}</Typography>
              <Button size="small" onClick={step(1)} sx={{ minWidth: 28, px: 0.5 }}>›</Button>
            </Box>
          )}
          {e.captured_at && (
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
              {String(e.captured_at).replace('T', ' ').slice(0, 16)}
            </Typography>
          )}
          {e.notes && <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>{e.notes}</Typography>}
        </Box>
      </Popup>
    </CircleMarker>
  );
}

// Coloured stage badge (1–6)
const STAGE_BG = ['', '#1565c0','#2e7d32','#6a1b9a','#e65100','#0277bd','#4e342e'];
function StageChip({ stage }) {
  return (
    <Box sx={{
      width: 15, height: 15, borderRadius: '3px', bgcolor: STAGE_BG[stage] || '#546e7a',
      color: '#fff', fontSize: 9, fontWeight: 800, lineHeight: '15px', textAlign: 'center',
      mr: 0.75, flexShrink: 0,
    }}>{stage}</Box>
  );
}

// Collapsible folder — Google Earth style
function FolderSection({ folderId, title, stage, icon, children, onExport, exportTitle, defaultOpen = true }) {
  const key = `map-folder-${folderId}`;
  const [open, setOpen] = useState(() => {
    try { const s = localStorage.getItem(key); return s !== null ? s === 'true' : defaultOpen; }
    catch { return defaultOpen; }
  });
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem(key, String(next)); } catch {}
  };
  return (
    <Box sx={{ mb: 0.5 }}>
      <Box onClick={toggle} sx={{ display: 'flex', alignItems: 'center', gap: 0.25, py: 0.5, px: 0.5, borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, userSelect: 'none' }}>
        <Iconify icon={open ? 'eva:arrow-down-fill' : 'eva:arrow-ios-forward-fill'} sx={{ width: 11, height: 11, color: 'text.disabled', flexShrink: 0 }} />
        {stage != null && <StageChip stage={stage} />}
        {stage == null && icon && <Iconify icon={icon} sx={{ width: 13, height: 13, mr: 0.75, color: 'text.secondary', flexShrink: 0 }} />}
        <Typography variant="caption" sx={{ fontWeight: 700, flex: 1, color: 'text.secondary', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.07em', lineHeight: 1 }}>
          {title}
        </Typography>
        {onExport && (
          <IconButton size="small" title={exportTitle || `Export ${title} as KML`} onClick={e => { e.stopPropagation(); onExport(); }}
            sx={{ p: 0.25, width: 20, height: 20, flexShrink: 0, color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
            <Iconify icon="eva:download-outline" sx={{ width: 13, height: 13 }} />
          </IconButton>
        )}
      </Box>
      {open && <Box sx={{ pl: 1.5 }}>{children}</Box>}
    </Box>
  );
}

function MapSidebar({
  basemap, onBasemapChange,
  showHistory, onShowHistory, waybackSnapshots, waybackLoading, selectedYear, onSelectedYear,
  hasBoundary, showBoundary, onBoundary,
  hasStrata, showStrata, onStrata, strataCount, primaryStrataId,
  hasPoints, showPoints, onPoints, primaryPointsId,
  hasEvidence, showEvidence, onEvidence,
  evidenceSeasons, hiddenSeasons, onToggleSeason, onToggleSeasonFolder,
  hasAnnotations, showAnnotations, onAnnotations, annotationCount,
  placing, onTogglePlacing,
  artifactGroups, activeLibraryIds, onToggleLibrary, loadingRasterIds,
  fieldRecords, activeFieldRecordId, onToggleFieldRecord, fieldRecordLoading,
  loading, onCollapse, onExport, projectCode,
  boundarySites = [], hiddenBoundarySites = new Set(), onToggleBoundarySite = () => {},
}) {
  // Which photo folders (gates / initial sampling) are expanded in the tree.
  const [expandedPhotoFolders, setExpandedPhotoFolders] = useState(new Set());
  const selectedSnap = waybackSnapshots.find(s => s.year === selectedYear);

  return (
    <Paper elevation={4} sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, zIndex: 1000, width: PANEL_WIDTH, display: 'flex', flexDirection: 'column', borderRadius: 0, bgcolor: 'background.paper', overflow: 'hidden' }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, pt: 1, pb: 0.5, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary', lineHeight: 1 }}>Layers</Typography>
        <IconButton size="small" onClick={onCollapse} aria-label="Hide panel">
          <Iconify icon="eva:arrow-ios-back-fill" sx={{ width: 18, height: 18 }} />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 1.5, pt: 1 }}>

        {/* Basemap */}
        <Typography variant="overline" sx={{ display: 'block', color: 'text.secondary', mb: 0.5, fontSize: '0.65rem' }}>Basemap</Typography>
        <ToggleButtonGroup value={basemap} exclusive onChange={(_, v) => { if (v) onBasemapChange(v); }}
          size="small" fullWidth sx={{ mb: basemap === 'satellite' ? 1 : 1.5 }}>
          <ToggleButton value="osm" sx={{ flex: 1, fontSize: 11 }}>
            <Iconify icon="eva:map-outline" sx={{ mr: 0.5, width: 14 }} />Map
          </ToggleButton>
          <ToggleButton value="satellite" sx={{ flex: 1, fontSize: 11 }}>
            <Iconify icon="eva:globe-2-outline" sx={{ mr: 0.5, width: 14 }} />Satellite
          </ToggleButton>
        </ToggleButtonGroup>

        {basemap === 'satellite' && (
          <Box sx={{ mb: 1.5 }}>
            <FormControlLabel sx={{ m: 0, mb: 0.5 }}
              control={<Checkbox size="small" checked={showHistory} onChange={e => onShowHistory(e.target.checked)} sx={{ py: 0.25 }} />}
              label={<Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>History (2014–now)</Typography>}
            />
            {showHistory && (
              <Box sx={{ px: 1 }}>
                {waybackLoading ? (
                  <Typography variant="caption" color="text.secondary">Loading…</Typography>
                ) : waybackSnapshots.length > 0 ? (
                  <>
                    <Slider size="small" min={waybackSnapshots[0].year} max={waybackSnapshots[waybackSnapshots.length - 1].year}
                      step={1} marks={waybackSnapshots.map(s => ({ value: s.year, label: '' }))}
                      value={selectedYear} onChange={(_, v) => onSelectedYear(v)} valueLabelDisplay="auto" valueLabelFormat={v => String(v)} />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: -0.5 }}>
                      {selectedSnap ? `${selectedSnap.year} — ${selectedSnap.date}` : selectedYear}
                    </Typography>
                  </>
                ) : <Typography variant="caption" color="error">Failed to load</Typography>}
              </Box>
            )}
          </Box>
        )}

        <Divider sx={{ mb: 1 }} />
        {loading && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Loading…</Typography>}

        {/* ── Stage 1: Site & Boundary ── */}
        <FolderSection folderId="site" title="Site" stage={1}
          onExport={hasBoundary ? () => onExport('boundary', {}) : undefined}
          exportTitle="Export boundary as KML">
          {hasBoundary
            ? <LayerRow show={showBoundary} onChange={onBoundary}><LayerDot color="#1976d2" /><Typography variant="body2" sx={{ fontSize: 12 }}>Boundary</Typography></LayerRow>
            : <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pb: 0.5 }}>No boundary yet</Typography>
          }
          {/* Project scope mirrors the data structure: Boundary → site → parcels
              (same hierarchy as the KML export and the DB). */}
          {showBoundary && boundarySites.length > 1 && boundarySites.map((s) => (
            <Box key={s} sx={{ pl: 2.5 }}>
              <LayerRow show={!hiddenBoundarySites.has(s)} onChange={() => onToggleBoundarySite(s)}>
                <LayerDot color="#64b5f6" />
                <Typography variant="body2" sx={{ fontSize: 11 }} noWrap title={s}>{s}</Typography>
              </LayerRow>
            </Box>
          ))}
        </FolderSection>

        {/* ── Stage 2: GEE Layers ── */}
        {artifactGroups.gee.length > 0 && (
          <FolderSection folderId="gee" title="GEE Layers" stage={2} defaultOpen={false}>
            {artifactGroups.gee.map(a => (
              <LayerRow key={a.id} show={activeLibraryIds.has(a.id)} onChange={() => onToggleLibrary(a.id)}>
                {loadingRasterIds.has(a.id)
                  ? <CircularProgress size={12} sx={{ mr: 0.75, flexShrink: 0, color: '#ff9800' }} />
                  : <Iconify icon="eva:layers-outline" sx={{ width: 12, height: 12, mr: 0.75, color: '#ff9800', flexShrink: 0 }} />}
                <Typography variant="body2" sx={{ fontSize: 11, lineHeight: 1.3 }} noWrap title={a.title}>{a.title || `Stack #${a.id}`}</Typography>
              </LayerRow>
            ))}
            <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', pt: 0.5, fontSize: 10, fontStyle: 'italic' }}>Raster — not exportable to KML</Typography>
          </FolderSection>
        )}

        {/* ── Stage 3: Stratification ── */}
        <FolderSection folderId="strat" title="Stratification" stage={3}
          onExport={primaryStrataId ? () => onExport('artifact', { id: primaryStrataId }) : undefined}
          exportTitle="Export strata as KML">
          {hasStrata
            ? <LayerRow show={showStrata} onChange={onStrata}>
                <Box sx={{ display: 'flex', mr: 0.75 }}>
                  {Array.from({ length: Math.min(strataCount, 5) }, (_, i) => (
                    <Box key={i} sx={{ width: 7, height: 11, bgcolor: strataColor(i + 1), borderRadius: '2px', mr: '1px' }} />
                  ))}
                </Box>
                <Typography variant="body2" sx={{ fontSize: 12 }}>Strata ({strataCount})</Typography>
              </LayerRow>
            : <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pb: 0.5 }}>No strata yet</Typography>
          }
          {artifactGroups.strat.map(a => (
            <LayerRow key={a.id} show={activeLibraryIds.has(a.id)} onChange={() => onToggleLibrary(a.id)}>
              <LayerDot color="#9c27b0" />
              <Typography variant="body2" sx={{ fontSize: 11, lineHeight: 1.3 }} noWrap title={a.title}>{a.title || `Strata #${a.id}`}</Typography>
            </LayerRow>
          ))}
        </FolderSection>

        {/* ── Stage 4: Sampling Points ── */}
        <FolderSection folderId="points" title="Sampling Points" stage={4}
          onExport={primaryPointsId ? () => onExport('artifact', { id: primaryPointsId }) : undefined}
          exportTitle="Export sampling points as KML">
          {hasPoints
            ? <LayerRow show={showPoints} onChange={onPoints}><LayerDot color="#555" /><Typography variant="body2" sx={{ fontSize: 12 }}>Sample Points</Typography></LayerRow>
            : <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pb: 0.5 }}>No points yet</Typography>
          }
          {artifactGroups.points.map(a => (
            <LayerRow key={a.id} show={activeLibraryIds.has(a.id)} onChange={() => onToggleLibrary(a.id)}>
              <LayerDot color="#555" />
              <Typography variant="body2" sx={{ fontSize: 11, lineHeight: 1.3 }} noWrap title={a.title}>{a.title || `Points #${a.id}`}</Typography>
            </LayerRow>
          ))}
        </FolderSection>

        {/* ── Stage 5: Campaigns (view-only on map) ── */}
        {artifactGroups.campaigns.length > 0 && (
          <FolderSection folderId="campaigns" title="Campaigns" stage={5} defaultOpen={false}>
            {artifactGroups.campaigns.map(a => (
              <Box key={a.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.25 }}>
                <Iconify icon="eva:calendar-fill" sx={{ width: 12, height: 12, color: '#0277bd', flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontSize: 11, flex: 1 }} noWrap title={a.title}>{a.title || `Campaign #${a.id}`}</Typography>
              </Box>
            ))}
            <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', pt: 0.25, fontSize: 10, fontStyle: 'italic' }}>Open in Sampling page to view on map</Typography>
          </FolderSection>
        )}

        {/* ── Stage 6: Field Records ── */}
        <FolderSection folderId="field-records" title="Field Records" stage={6}
          onExport={activeFieldRecordId ? () => onExport('field-record', { id: activeFieldRecordId }) : undefined}
          exportTitle="Export active field record as KML">
          {fieldRecords.length === 0
            ? <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pb: 0.5 }}>No field records yet</Typography>
            : fieldRecords.map(fr => (
              <LayerRow key={fr.id} show={activeFieldRecordId === fr.id} onChange={() => onToggleFieldRecord(fr.id)}>
                {fieldRecordLoading && activeFieldRecordId === fr.id
                  ? <CircularProgress size={11} sx={{ mr: 0.75, flexShrink: 0 }} />
                  : <LayerDot color="#4caf50" />
                }
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" noWrap title={fr.campaign_name} sx={{ fontSize: 12 }}>{fr.campaign_name}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: 10 }}>
                    {String(fr.date).slice(0, 10)} · {fr.done_points}/{fr.total_points} pts
                  </Typography>
                </Box>
              </LayerRow>
            ))
          }
        </FolderSection>

        {/* ── Evidence ── */}
        <FolderSection folderId="evidence" title="Evidence" icon="eva:camera-fill"
          onExport={hasEvidence ? () => onExport('evidence', {}) : undefined}
          exportTitle="Export evidence photos as KML">
          {hasEvidence
            ? (
              <>
                <LayerRow show={showEvidence} onChange={onEvidence}><LayerDot color="#ff5722" /><Typography variant="body2" sx={{ fontSize: 12 }}>Photos</Typography></LayerRow>
                {/* Two-level tree mirroring the checklist: gate folders (A, C, …)
                    with their tasks inside, and "Initial soil sampling" holding the
                    pre-task-era captures grouped by measurement unit. Folder
                    checkbox toggles all children; caret expands them. */}
                {showEvidence && evidenceSeasons.map((f) => {
                  const open = expandedPhotoFolders.has(f.key);
                  const hiddenN = f.children.filter(c => hiddenSeasons.has(c.key)).length;
                  const allHidden = hiddenN === f.children.length;
                  const someHidden = hiddenN > 0 && !allHidden;
                  return (
                    <Box key={f.key} sx={{ pl: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton size="small" sx={{ p: 0.25 }} onClick={() => setExpandedPhotoFolders(prev => {
                          const next = new Set(prev);
                          if (next.has(f.key)) next.delete(f.key); else next.add(f.key);
                          return next;
                        })}>
                          <Iconify icon={open ? 'eva:arrow-down-fill' : 'eva:arrow-ios-forward-fill'} sx={{ width: 11, height: 11, color: 'text.disabled' }} />
                        </IconButton>
                        <FormControlLabel sx={{ m: 0, flex: 1, minHeight: 28 }}
                          control={<Checkbox size="small" checked={!allHidden} indeterminate={someHidden}
                            onChange={() => onToggleSeasonFolder(f.key)} sx={{ py: 0.25, pr: 0.5 }} />}
                          label={<Typography variant="body2" sx={{ fontSize: 12, fontWeight: 600 }}>{f.label} ({f.count})</Typography>}
                        />
                      </Box>
                      {open && f.children.map((c) => (
                        <Box key={c.key} sx={{ pl: 4 }}>
                          <LayerRow show={!hiddenSeasons.has(c.key)} onChange={() => onToggleSeason(c.key)}>
                            <LayerDot color={photoColor(c.key)} />
                            <Typography variant="body2" sx={{ fontSize: 12 }}>{c.label} ({c.count})</Typography>
                          </LayerRow>
                        </Box>
                      ))}
                    </Box>
                  );
                })}
              </>
            )
            : <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pb: 0.5 }}>No photos with GPS yet</Typography>
          }
        </FolderSection>

        {/* ── Annotations ── */}
        <FolderSection folderId="annotations" title="Annotations" icon="eva:pin-fill"
          onExport={hasAnnotations ? () => onExport('annotations', {}) : undefined}>
          {hasAnnotations && (
            <LayerRow show={showAnnotations} onChange={onAnnotations}><LayerDot color="#ffd600" /><Typography variant="body2" sx={{ fontSize: 12 }}>Markers ({annotationCount})</Typography></LayerRow>
          )}
          <Button variant={placing ? 'contained' : 'outlined'} color={placing ? 'warning' : 'primary'}
            size="small" fullWidth startIcon={<Iconify icon={placing ? 'eva:close-outline' : 'eva:pin-outline'} />}
            onClick={onTogglePlacing} sx={{ mt: 0.5, fontSize: 11 }}>
            {placing ? 'Cancel — click map' : 'Place Marker'}
          </Button>
          {placing && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'center' }}>Click anywhere on the map</Typography>}
        </FolderSection>

      </Box>

      {/* Footer — Google Earth. Downloads-for-editing and their return live in
          Project Settings → Geometry Review (one entry point, Yuval 26/09);
          the map keeps only the live view link. */}
      <Box sx={{ px: 1.5, py: 1, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Button fullWidth size="small" variant="contained" color="primary" onClick={() => onExport('network-link', {})}
          startIcon={<Iconify icon="eva:globe-2-outline" sx={{ width: 14 }} />} sx={{ fontSize: 11 }}>
          Live-link KML — current view
        </Button>
      </Box>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Main MapView
// ---------------------------------------------------------------------------
export default function MapView({ projectCode, projectId, siteCode, siteId }) {
  const [boundary, setBoundary] = useState(null);
  const [hiddenBoundarySites, setHiddenBoundarySites] = useState(new Set());
  const [strata, setStrata] = useState(null);
  const [points, setPoints] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [annotations, setAnnotations] = useState([]);
  const [allArtifacts, setAllArtifacts] = useState([]);
  const [libraryPayloads, setLibraryPayloads] = useState({});
  const [activeLibraryIds, setActiveLibraryIds] = useState(new Set());
  const [loadingRasterIds, setLoadingRasterIds] = useState(new Set());
  const [loading, setLoading] = useState(false);

  // Basemap & history
  const [basemap, setBasemap] = useState('satellite');
  const [showHistory, setShowHistory] = useState(false);
  const [waybackSnapshots, setWaybackSnapshots] = useState([]);
  const [waybackLoading, setWaybackLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Layer visibility
  const [showBoundary, setShowBoundary] = useState(true);
  const [showStrata, setShowStrata] = useState(true);
  const [showPoints, setShowPoints] = useState(true);
  const [showEvidence, setShowEvidence] = useState(true);
  // ?evidence=<id> (from the field tool's "see on map" links): fly to that
  // capture and ring it so the operator finds their photo instantly.
  const focusEvidenceId = useMemo(() => {
    try { return new URLSearchParams(window.location.search).get('evidence'); }
    catch { return null; }
  }, []);
  const focusPt = useMemo(() => {
    if (!focusEvidenceId) return null;
    const e = evidence.find((x) => String(x.id) === focusEvidenceId);
    return e && e.lat && e.lng ? { lat: Number(e.lat), lng: Number(e.lng) } : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusEvidenceId, evidence]);
  const [showAnnotations, setShowAnnotations] = useState(true);

  // Field records
  const [fieldRecords, setFieldRecords] = useState([]);
  const [activeFieldRecordId, setActiveFieldRecordId] = useState(null);
  const [fieldRecordDetail, setFieldRecordDetail] = useState(null);
  const [fieldRecordLoading, setFieldRecordLoading] = useState(false);

  // Sidebar collapse — default closed on mobile
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 768);

  // User location
  const [userLocation, setUserLocation] = useState(null); // {lat, lng, accuracy}

  // Annotations
  const [placing, setPlacing] = useState(false);
  const [pendingLatLng, setPendingLatLng] = useState(null);
  const [editingAnnotation, setEditingAnnotation] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // ---------------------------------------------------------------------------
  // Load Wayback snapshots when history is first enabled
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!showHistory || waybackSnapshots.length > 0) return;
    setWaybackLoading(true);
    fetchWaybackSnapshots()
      .then(snaps => {
        setWaybackSnapshots(snaps);
        // Default to the most recent year
        if (snaps.length) setSelectedYear(snaps[snaps.length - 1].year);
      })
      .catch(() => {})
      .finally(() => setWaybackLoading(false));
  }, [showHistory, waybackSnapshots.length]);

  // ---------------------------------------------------------------------------
  // Load project data
  // ---------------------------------------------------------------------------
  const loadAnnotations = useCallback(async () => {
    if (!projectId) return;
    const siteIdQ = siteId ? `&siteId=${siteId}` : '';
    const r = await fetch(`/v1/map-annotations?projectId=${projectId}${siteIdQ}`).catch(() => null);
    if (r?.ok) setAnnotations(await r.json());
  }, [projectId, siteId]);

  useEffect(() => {
    if (!projectCode || !projectId) return;
    setLoading(true);
    setBoundary(null); setStrata(null); setPoints([]); setEvidence([]);
    setAnnotations([]); setAllArtifacts([]); setLibraryPayloads({}); setActiveLibraryIds(new Set());
    setHiddenBoundarySites(new Set());

    const siteQ = siteCode ? `&site=${siteCode}` : '';
    const siteIdQ = siteId ? `&siteId=${siteId}` : '';

    Promise.all([
      fetch(`/v1/stratification/geometry?project=${projectCode}${siteQ}`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`/v1/stratification/artifacts?project=${projectCode}`).then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(`/v1/evidence?project=${projectCode}`).then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(`/v1/map-annotations?projectId=${projectId}${siteIdQ}`).then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(`/v1/field-records?project=${projectCode}${siteQ}`).then(r => r.ok ? r.json() : []).catch(() => []),
    ]).then(async ([geo, artifacts, evArr, annArr, frArr]) => {
      setBoundary(geo || null);
      setEvidence((evArr || []).filter(e => e.lat && e.lng));
      setAnnotations(annArr || []);
      setFieldRecords(frArr || []);
      const sorted = (artifacts || []).sort((a, b) => b.id - a.id);
      setAllArtifacts(sorted);

      // Only true stratification OUTPUT may be the primary Strata layer. Uploaded
      // field-geometry KMLs (pilot sites, controls) also carry subtype 'strata' and,
      // newest-id-wins, would silently REPLACE the stratification everywhere.
      const strataArt = sorted.find(a => a.tool === 'stratification' && a.subtype === 'strata');
      if (strataArt) {
        const r = await fetch(`/v1/stratification/artifacts/${strataArt.id}`).catch(() => null);
        if (r?.ok) {
          const features = parseArtifactFeatures(await r.json());
          if (features.length) setStrata({ type: 'FeatureCollection', features });
        }
      }
      const ptsArt = sorted.find(a => a.subtype === 'points');
      if (ptsArt) {
        const r = await fetch(`/v1/stratification/artifacts/${ptsArt.id}`).catch(() => null);
        if (r?.ok) {
          const features = parseArtifactFeatures(await r.json()).filter(f => f.geometry?.type === 'Point');
          if (features.length) setPoints(features);
        }
      }
    }).finally(() => setLoading(false));
  }, [projectCode, projectId, siteCode, siteId]);

  // ---------------------------------------------------------------------------
  // Library toggle
  // ---------------------------------------------------------------------------
  const handleToggleLibrary = useCallback(async (id) => {
    const art = allArtifacts.find(a => a.id === id);
    const turningOn = !activeLibraryIds.has(id);
    setActiveLibraryIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
    if (turningOn) {
      // Mark raster as loading when turned on
      if (art?.subtype === 'stack') {
        setLoadingRasterIds(prev => new Set([...prev, id]));
      }
      // Raster artifacts are streamed directly via URL — no GeoJSON payload needed
      if (art?.subtype !== 'stack' && !libraryPayloads[id]) {
        const r = await fetch(`/v1/stratification/artifacts/${id}`).catch(() => null);
        if (r?.ok) {
          const features = parseArtifactFeatures(await r.json());
          if (features.length) setLibraryPayloads(prev => ({ ...prev, [id]: features }));
        }
      }
    } else {
      // Remove loading indicator when turning off
      setLoadingRasterIds(prev => { const n = new Set(prev); n.delete(id); return n; });
    }
  }, [libraryPayloads, allArtifacts, activeLibraryIds]);

  // ---------------------------------------------------------------------------
  // User location watch
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition(
      pos => setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      () => {},
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  // ---------------------------------------------------------------------------
  // Field record toggle
  // ---------------------------------------------------------------------------
  const handleToggleFieldRecord = useCallback(async (id) => {
    if (activeFieldRecordId === id) {
      setActiveFieldRecordId(null);
      setFieldRecordDetail(null);
      return;
    }
    setActiveFieldRecordId(id);
    setFieldRecordDetail(null);
    setFieldRecordLoading(true);
    const r = await fetch(`/v1/field-records/${id}`).catch(() => null);
    if (r?.ok) setFieldRecordDetail(await r.json());
    setFieldRecordLoading(false);
  }, [activeFieldRecordId]);

  // ---------------------------------------------------------------------------
  // KML export / Google Earth
  // ---------------------------------------------------------------------------
  // The layers currently VISIBLE on the map — what-you-see-is-what-you-export.
  const currentViewLayers = useCallback(() => {
    const l = [];
    if (showBoundary) l.push('boundary');
    if (allArtifacts.some(a => a.subtype === 'strata' && a.tool !== 'stratification' && activeLibraryIds.has(a.id))) l.push('fields');
    if (showPoints) l.push('points');
    if (activeFieldRecordId) l.push('records');
    if (showEvidence) l.push('evidence');
    if (showAnnotations) l.push('annotations');
    return l.join(',');
  }, [showBoundary, showPoints, showEvidence, showAnnotations, activeFieldRecordId, allArtifacts, activeLibraryIds]);

  const handleExport = useCallback((type, params = {}) => {
    let url;
    const scoped = new URLSearchParams({ project: projectCode });
    if (siteCode) scoped.set('site', siteCode);
    const layers = currentViewLayers();
    if (layers) scoped.set('layers', layers);
    if (type === 'artifact') {
      url = `/v1/stratification/artifacts/${params.id}/kml`;
    } else if (type === 'evidence') {
      url = `/evidence.kml?projectId=${projectCode}`;
    } else if (type === 'network-link') {
      url = `/v1/export/network-link?${scoped.toString()}`;
    } else if (type === 'project') {
      url = `/v1/export/kml/project?${scoped.toString()}`;
    } else if (type === 'project-edit') {
      // Editing export: embeds the manifest id the review page diffs against —
      // only THIS file is accepted back by "Review external edit".
      scoped.set('for', 'editing');
      url = `/v1/export/kml/project?${scoped.toString()}`;
    } else {
      const qs = new URLSearchParams({ project: projectCode, ...params }).toString();
      url = `/v1/export/kml/${type}?${qs}`;
    }
    const a = document.createElement('a');
    a.href = url;
    a.download = `terrra-${type}-${projectCode}.kml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [projectCode, siteCode, currentViewLayers]);

  // ---------------------------------------------------------------------------
  // Annotation CRUD
  // ---------------------------------------------------------------------------
  const handleMapClick = useCallback((latlng) => {
    setPendingLatLng(latlng); setEditingAnnotation(null); setDialogOpen(true);
  }, []);

  const handleDialogSave = useCallback(async (form) => {
    if (editingAnnotation) {
      const r = await fetch(`/v1/map-annotations/${editingAnnotation.id}`, {
        method: 'PUT', headers: bearerHeaders(), body: JSON.stringify(form),
      }).catch(() => null);
      if (r?.ok) { await loadAnnotations(); setDialogOpen(false); setEditingAnnotation(null); }
    } else {
      const r = await fetch('/v1/map-annotations', {
        method: 'POST', headers: bearerHeaders(),
        body: JSON.stringify({ projectId, siteId: siteId || null, lat: pendingLatLng.lat, lng: pendingLatLng.lng, ...form }),
      }).catch(() => null);
      if (r?.ok) { await loadAnnotations(); setDialogOpen(false); setPlacing(false); setPendingLatLng(null); }
    }
  }, [editingAnnotation, pendingLatLng, projectId, siteId, loadAnnotations]);

  const handleDelete = useCallback(async (annotationId) => {
    const r = await fetch(`/v1/map-annotations/${annotationId}`, { method: 'DELETE', headers: bearerHeaders() }).catch(() => null);
    if (r?.ok) await loadAnnotations();
  }, [loadAnnotations]);

  // ---------------------------------------------------------------------------
  // Styles
  // ---------------------------------------------------------------------------
  const strataStyle = useCallback((feature) => ({
    fillColor: strataColor(feature.properties?.stratum),
    fillOpacity: 0.55, color: 'rgba(255,255,255,0.5)', weight: 0.5,
  }), []);
  const boundaryStyle = { fillColor: 'transparent', color: '#1976d2', weight: 2.5, dashArray: '8 5' };

  const hasStrata = Boolean(strata?.features?.length);
  const hasPoints = points.length > 0;
  const hasEvidence = evidence.length > 0;

  // Co-located captures used to paint one circle on top of another, so three photos
  // of one sensor looked like one photo and "did it arrive?" was unanswerable.
  //
  // Cluster by PROXIMITY, not by rounded coordinates. Rounding puts a hard grid line
  // through continuous space: an operator walking a parcel at ±5 m GPS had five photos
  // of one task split across three boxes, with two 9 m apart grouped while two at the
  // same spot were separated — whichever side of the line they landed on.
  // Per-task-group show/hide for the photo layer (e.g. park "Initial soil sampling"
  // while reviewing the current campaign). Empty set = everything visible.
  const [hiddenSeasons, setHiddenSeasons] = useState(new Set());
  const toggleSeason = (key) => setHiddenSeasons(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const evidenceSeasons = useMemo(() => {
    const folders = new Map();
    evidence.forEach((e) => {
      const l = photoLeafOf(e);
      let f = folders.get(l.folder);
      if (!f) {
        f = { key: l.folder, label: l.folderLabel, sort: l.folderSort, count: 0, children: new Map() };
        folders.set(l.folder, f);
      }
      f.count += 1;
      const c = f.children.get(l.key);
      if (c) c.count += 1; else f.children.set(l.key, { key: l.key, label: l.label, sort: l.sort, count: 1 });
    });
    return Array.from(folders.values())
      .sort((a, b) => a.sort.localeCompare(b.sort))
      .map(f => ({ ...f, children: Array.from(f.children.values()).sort((a, b) => a.sort.localeCompare(b.sort)) }));
  }, [evidence]);

  // Folder checkbox = all children at once (indeterminate when mixed).
  const toggleSeasonFolder = (folderKey) => {
    const f = evidenceSeasons.find(x => x.key === folderKey);
    if (!f) return;
    setHiddenSeasons(prev => {
      const next = new Set(prev);
      const anyVisible = f.children.some(c => !next.has(c.key));
      f.children.forEach(c => { if (anyVisible) next.add(c.key); else next.delete(c.key); });
      return next;
    });
  };

  const evidenceGroups = useMemo(() => {
    const RADIUS_M = 25;      // a person working one spot, plus GPS scatter
    const groups = [];
    const visible = hiddenSeasons.size
      ? evidence.filter(e => !hiddenSeasons.has(photoLeafOf(e).key))
      : evidence;
    // Oldest first, so the first capture of a task seeds its cluster.
    const sorted = [...visible].sort((a, b) => String(a.captured_at || '').localeCompare(String(b.captured_at || '')));
    sorted.forEach((e) => {
      const lat = parseFloat(e.lat), lng = parseFloat(e.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const tk = e.task_id || e.sampling_point || e.category || '';
      const near = groups.find(g => g.tk === tk && metresApart(g.lat, g.lng, lat, lng) <= RADIUS_M);
      if (near) {
        near.items.push(e);
        // Track the centroid so a cluster doesn't drift toward whichever photo came first.
        near.latSum += lat; near.lngSum += lng;
        near.lat = near.latSum / near.items.length;
        near.lng = near.lngSum / near.items.length;
      } else {
        groups.push({ key: `${tk}|${e.id}`, tk, lat, lng, latSum: lat, lngSum: lng, items: [e] });
      }
    });
    return groups;
  }, [evidence, hiddenSeasons]);
  const hasBoundary = Boolean(boundary);
  const hasAnnotations = annotations.length > 0;
  const strataCount = hasStrata ? new Set(strata.features.map(f => f.properties?.stratum)).size : 0;

  // Review-born artifacts (params.source === 'geometry-review') are additions,
  // never the canonical layer — same rule as the KML export composer.
  const notReviewArt = (a) => {
    try { const p = typeof a.params_json === 'string' ? JSON.parse(a.params_json) : a.params_json; return !p || p.source !== 'geometry-review'; }
    catch { return true; }
  };
  const primaryStrataArt = allArtifacts.find(a => a.tool === 'stratification' && a.subtype === 'strata');
  const primaryPointsArt = allArtifacts.find(a => a.subtype === 'points' && notReviewArt(a));

  // Project scope: the boundary is Boundary → site → parcels (the data
  // structure); each site gets its own visibility toggle. Site scope: one site,
  // no grouping.
  const boundarySites = !siteCode && boundary?.features
    ? [...new Set(boundary.features.map(f => f.properties?.site_name).filter(Boolean))]
    : [];
  const toggleBoundarySite = useCallback((s) => {
    setHiddenBoundarySites(prev => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s); else next.add(s);
      return next;
    });
  }, []);
  const visibleBoundary = boundary && hiddenBoundarySites.size && boundary.features
    ? { ...boundary, features: boundary.features.filter(f => !hiddenBoundarySites.has(f.properties?.site_name)) }
    : boundary;
  const autoLoadedIds = new Set([primaryStrataArt?.id, primaryPointsArt?.id].filter(Boolean));
  const libraryArtifacts = allArtifacts.filter(a => !autoLoadedIds.has(a.id));
  const artifactGroups = {
    gee:       libraryArtifacts.filter(a => a.tool === 'time-explorer'),
    // Polygon uploads (pilot sites, controls) belong with stratification in the
    // sidebar, whatever tool the upload path stamped them with.
    strat:     libraryArtifacts.filter(a => a.tool === 'stratification' || a.subtype === 'strata'),
    points:    libraryArtifacts.filter(a => a.tool === 'sampling-points' && a.subtype !== 'strata'),
    campaigns: libraryArtifacts.filter(a => a.tool === 'sampling-campaign'),
  };

  // Resolve active tile URL
  const activeSnap = showHistory && basemap === 'satellite'
    ? waybackSnapshots.find(s => s.year === selectedYear) || waybackSnapshots[waybackSnapshots.length - 1]
    : null;
  const activeTileUrl = activeSnap ? activeSnap.tileUrl : (BASEMAPS[basemap]?.url || BASEMAPS.satellite.url);
  const activeAttribution = activeSnap
    ? `ESRI World Imagery Wayback — ${activeSnap.date}`
    : (BASEMAPS[basemap]?.attribution || SATELLITE_ATTR);

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', display: 'flex', overflow: 'hidden' }}>

      {/* Collapsible sidebar */}
      <Box sx={{
        position: 'absolute', left: 0, top: 0, bottom: 0, zIndex: 1001,
        transform: sidebarOpen ? 'translateX(0)' : `translateX(-${PANEL_WIDTH}px)`,
        transition: 'transform 0.25s ease',
        width: PANEL_WIDTH,
      }}>
        <MapSidebar
          basemap={basemap} onBasemapChange={setBasemap}
          showHistory={showHistory} onShowHistory={setShowHistory}
          waybackSnapshots={waybackSnapshots} waybackLoading={waybackLoading}
          selectedYear={selectedYear} onSelectedYear={setSelectedYear}
          hasBoundary={hasBoundary} showBoundary={showBoundary} onBoundary={setShowBoundary}
          hasStrata={hasStrata} showStrata={showStrata} onStrata={setShowStrata} strataCount={strataCount}
          primaryStrataId={primaryStrataArt?.id} primaryPointsId={primaryPointsArt?.id}
          hasPoints={hasPoints} showPoints={showPoints} onPoints={setShowPoints}
          hasEvidence={hasEvidence} showEvidence={showEvidence} onEvidence={setShowEvidence}
          evidenceSeasons={evidenceSeasons} hiddenSeasons={hiddenSeasons} onToggleSeason={toggleSeason} onToggleSeasonFolder={toggleSeasonFolder}
          hasAnnotations={hasAnnotations} showAnnotations={showAnnotations} onAnnotations={setShowAnnotations} annotationCount={annotations.length}
          placing={placing} onTogglePlacing={() => setPlacing(v => !v)}
          artifactGroups={artifactGroups} activeLibraryIds={activeLibraryIds}
          onToggleLibrary={handleToggleLibrary} loadingRasterIds={loadingRasterIds}
          fieldRecords={fieldRecords} activeFieldRecordId={activeFieldRecordId}
          onToggleFieldRecord={handleToggleFieldRecord} fieldRecordLoading={fieldRecordLoading}
          loading={loading} onCollapse={() => setSidebarOpen(false)}
          onExport={handleExport} projectCode={projectCode}
          boundarySites={boundarySites} hiddenBoundarySites={hiddenBoundarySites}
          onToggleBoundarySite={toggleBoundarySite}
        />
      </Box>

      {/* Floating open button — visible when sidebar is hidden */}
      {!sidebarOpen && (
        <Box sx={{ position: 'absolute', top: 12, left: 12, zIndex: 1002 }}>
          <IconButton
            onClick={() => setSidebarOpen(true)}
            aria-label="Show layers panel"
            sx={{
              bgcolor: 'background.paper',
              boxShadow: 3,
              width: 40, height: 40,
              '&:hover': { bgcolor: 'grey.100' },
            }}
          >
            <Iconify icon="eva:layers-fill" sx={{ width: 20, height: 20 }} />
          </IconButton>
        </Box>
      )}

      <Box sx={{
        position: 'absolute', left: 0, right: 0, top: 0, bottom: 0,
        ml: sidebarOpen ? `${PANEL_WIDTH}px` : 0,
        transition: 'margin-left 0.25s ease',
        cursor: placing ? 'crosshair' : undefined,
      }}>
        <MapContainer center={[20, 15]} zoom={3} style={{ width: '100%', height: '100%' }} zoomControl>
          <TileLayer key={activeTileUrl} url={activeTileUrl} attribution={activeAttribution} />
          <MapClickHandler placing={placing} onMapClick={handleMapClick} />
          {boundary && !focusPt && <BoundsFitter boundary={boundary} />}
          {focusPt && <FlyToPoint pt={focusPt} />}
          {focusPt && (
            <CircleMarker center={[focusPt.lat, focusPt.lng]} radius={22}
              pathOptions={{ color: '#ff8f00', weight: 3, fill: false, dashArray: '6 4' }} />
          )}

          {showBoundary && hasBoundary && (
            <GeoJSON key={`boundary-${[...hiddenBoundarySites].sort().join('|')}`} data={visibleBoundary} style={boundaryStyle} />
          )}

          {showStrata && hasStrata && (
            <GeoJSON key={`strata-${strata.features.length}`} data={strata} style={strataStyle}
              onEachFeature={bindPolygonPopup} />
          )}

          {showPoints && hasPoints && points.map((f, i) => {
            const [lng, lat] = f.geometry.coordinates;
            const stratum = f.properties?.stratum;
            const pointName = f.properties?.label || f.properties?.name;
            // Named strata already read "Stratum A" — don't prefix them again. The
            // "(N)" a KML folder name carries is its point count, noise on one point.
            const stratumName = stratum != null ? String(stratum).replace(/\s*\(\d+\)\s*$/, '') : null;
            const stratumLine = stratumName
              ? (/stratum/i.test(stratumName) ? stratumName : `Stratum ${stratumName}`)
              : null;
            return (
              <CircleMarker key={i} center={[lat, lng]} radius={5}
                pathOptions={{ color: '#fff', weight: 1.5, fillColor: strataColor(stratum), fillOpacity: 1 }}>
                {(pointName || stratumLine) && (
                  <Popup>
                    {pointName && <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>{pointName}</Typography>}
                    {stratumLine && <Typography variant="caption">{stratumLine}</Typography>}
                  </Popup>
                )}
              </CircleMarker>
            );
          })}

          {showEvidence && hasEvidence && evidenceGroups.map((g) => (
            <EvidenceGroupMarker key={g.key} group={g} />
          ))}

          {showAnnotations && annotations.map((a) => {
            const color = annotationColor(a.type);
            const typeDef = ANNOTATION_TYPES.find(t => t.key === a.type);
            return (
              <CircleMarker key={a.id} center={[parseFloat(a.lat), parseFloat(a.lng)]} radius={9}
                pathOptions={{ color: '#fff', weight: 2.5, fillColor: color, fillOpacity: 1 }}>
                {a.label && <Tooltip permanent direction="top" offset={[0, -10]}>{a.label}</Tooltip>}
                <Popup maxWidth={240}>
                  <Box sx={{ minWidth: 180 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, mr: 0.75, flexShrink: 0 }} />
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {typeDef?.label || a.type}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>{a.label}</Typography>
                    {a.notes && <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.75 }}>{a.notes}</Typography>}
                    {a.created_by_email && (
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', mb: 0.75 }}>{a.created_by_email}</Typography>
                    )}
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                      <Button size="small" onClick={() => { setEditingAnnotation(a); setDialogOpen(true); }}>Edit</Button>
                      <Button size="small" color="error" onClick={() => handleDelete(a.id)}>Delete</Button>
                    </Stack>
                  </Box>
                </Popup>
              </CircleMarker>
            );
          })}

          {/* Field record layer */}
          {fieldRecordDetail && (() => {
            const pts = fieldRecordDetail.points || [];
            const frEvidence = evidence.filter(e => e.field_record_id === activeFieldRecordId && e.lat && e.lng);
            return (
              <>
                {/* Displacement lines: planned → actual */}
                {pts.filter(p => p.planned_lat && p.planned_lng && p.actual_lat && p.actual_lng).map(p => (
                  <Polyline key={`fr-disp-${p.id}`}
                    positions={[[parseFloat(p.planned_lat), parseFloat(p.planned_lng)], [parseFloat(p.actual_lat), parseFloat(p.actual_lng)]]}
                    pathOptions={{ color: '#90caf9', weight: 1.5, dashArray: '5 5', opacity: 0.8 }} />
                ))}

                {/* Planned positions — hollow ring */}
                {pts.filter(p => p.planned_lat && p.planned_lng).map(p => {
                  const color = frStatusColor(p.status);
                  const sampleIds = (() => { try { const a = typeof p.sample_ids === 'string' ? JSON.parse(p.sample_ids) : (p.sample_ids || []); return Array.isArray(a) ? a.join(', ') : ''; } catch { return ''; } })();
                  return (
                    <CircleMarker key={`fr-plan-${p.id}`}
                      center={[parseFloat(p.planned_lat), parseFloat(p.planned_lng)]} radius={9}
                      pathOptions={{ color, weight: 2.5, fillColor: color, fillOpacity: 0.15 }}>
                      <Popup maxWidth={200}>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>{p.status}</Typography>
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{p.planned_name}</Typography>
                          {sampleIds && <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}>Samples: {sampleIds}</Typography>}
                          {p.notes && <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}>{p.notes}</Typography>}
                          {p.evidence_count > 0 && <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#9c27b0' }}>📷 {p.evidence_count} photo{p.evidence_count > 1 ? 's' : ''}</Typography>}
                        </Box>
                      </Popup>
                    </CircleMarker>
                  );
                })}

                {/* Actual GPS positions — solid fill */}
                {pts.filter(p => p.actual_lat && p.actual_lng).map(p => {
                  const color = frStatusColor(p.status);
                  return (
                    <CircleMarker key={`fr-act-${p.id}`}
                      center={[parseFloat(p.actual_lat), parseFloat(p.actual_lng)]} radius={6}
                      pathOptions={{ color: '#fff', weight: 1.5, fillColor: color, fillOpacity: 1 }}>
                      <Tooltip permanent direction="top" offset={[0, -8]}>
                        <span style={{ fontSize: 11, fontWeight: 700 }}>{p.planned_name}</span>
                      </Tooltip>
                    </CircleMarker>
                  );
                })}

                {/* Evidence photos linked to this field record — plotted at actual GPS capture location */}
                {frEvidence.map(e => {
                  // Check if photo GPS differs significantly from its assigned point's planned position
                  const assignedPt = pts.find(p => p.planned_name === e.sampling_point);
                  const distM = assignedPt && assignedPt.planned_lat
                    ? Math.round(Math.hypot(
                        (parseFloat(e.lat) - parseFloat(assignedPt.planned_lat)) * 111320,
                        (parseFloat(e.lng) - parseFloat(assignedPt.planned_lng)) * 111320 * Math.cos(parseFloat(e.lat) * Math.PI / 180)
                      ))
                    : null;
                  const mismatch = distM !== null && distM > 30;
                  return (
                    <CircleMarker key={`fr-ev-${e.id}`}
                      center={[parseFloat(e.lat), parseFloat(e.lng)]} radius={10}
                      pathOptions={{ color: mismatch ? '#ff9800' : '#fff', weight: 2, fillColor: '#9c27b0', fillOpacity: 0.95 }}>
                      <Popup maxWidth={250}>
                        <Box sx={{ textAlign: 'center', minWidth: 190 }}>
                          <img src={`/v1/evidence/${e.id}/image`} alt={e.sampling_point || 'Photo'}
                            style={{ width: 230, maxHeight: 172, objectFit: 'cover', borderRadius: 4, display: 'block', marginBottom: 6 }}
                            onError={ev => { ev.target.style.display = 'none'; }} />
                          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                            Tagged: {e.sampling_point || e.category || 'Field photo'}
                          </Typography>
                          {mismatch && (
                            <Typography variant="caption" sx={{ display: 'block', color: 'warning.main', fontWeight: 600, mt: 0.25 }}>
                              ⚠ {distM}m from {e.sampling_point} planned position
                            </Typography>
                          )}
                          {e.notes && <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}>{e.notes}</Typography>}
                          {e.captured_at && <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', mt: 0.25 }}>{new Date(e.captured_at).toLocaleString()}</Typography>}
                        </Box>
                      </Popup>
                    </CircleMarker>
                  );
                })}
              </>
            );
          })()}

          {/* Library layers */}
          {Array.from(activeLibraryIds).map(id => {
            const art = allArtifacts.find(a => a.id === id);
            // Raster (stack) artifacts — stream via dedicated endpoint
            if (art?.subtype === 'stack') {
              return <GeoRasterLayer key={`raster-${id}`} url={`/v1/stratification/artifacts/${id}/raster-file`}
                onLoad={() => setLoadingRasterIds(prev => { const n = new Set(prev); n.delete(id); return n; })} />;
            }
            const features = libraryPayloads[id];
            if (!features?.length) return null;
            const polygons = features.filter(f => f.geometry?.type === 'Polygon' || f.geometry?.type === 'MultiPolygon');
            const pts = features.filter(f => f.geometry?.type === 'Point');
            return (
              <Box key={id} component="span">
                {polygons.length > 0 && (
                  <GeoJSON key={`lib-poly-${id}`}
                    data={{ type: 'FeatureCollection', features: polygons }}
                    style={(f) => {
                      // Control land must never read as project land: dashed grey.
                      if (/control/i.test(String(f.properties?.stratum || ''))) {
                        return { fillColor: '#9e9e9e', fillOpacity: 0.15, color: '#757575', weight: 1.5, dashArray: '6 4' };
                      }
                      return { fillColor: strataColor(f.properties?.stratum), fillOpacity: 0.5, color: 'rgba(255,255,255,0.5)', weight: 0.5 };
                    }}
                    onEachFeature={bindPolygonPopup}
                  />
                )}
                {pts.map((f, i) => {
                  const [lng, lat] = f.geometry.coordinates;
                  return (
                    <CircleMarker key={`lib-pt-${id}-${i}`} center={[lat, lng]} radius={5}
                      pathOptions={{ color: '#fff', weight: 1.5, fillColor: '#9c27b0', fillOpacity: 0.9 }}>
                      {f.properties?.label && <Popup><Typography variant="caption">{f.properties.label}</Typography></Popup>}
                    </CircleMarker>
                  );
                })}
              </Box>
            );
          })}
          {/* User location */}
          {userLocation && (
            <>
              {/* Accuracy circle */}
              <CircleMarker
                center={[userLocation.lat, userLocation.lng]}
                radius={Math.min(Math.max(userLocation.accuracy / 2, 8), 60)}
                pathOptions={{ color: '#1976d2', weight: 1, fillColor: '#1976d2', fillOpacity: 0.1 }}
              />
              {/* Position dot */}
              <CircleMarker
                center={[userLocation.lat, userLocation.lng]}
                radius={8}
                pathOptions={{ color: '#fff', weight: 2.5, fillColor: '#1976d2', fillOpacity: 1 }}>
                <Popup>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>You are here</Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                    ±{Math.round(userLocation.accuracy)}m accuracy
                  </Typography>
                </Popup>
              </CircleMarker>
            </>
          )}
        </MapContainer>
      </Box>

      <AnnotationDialog
        open={dialogOpen}
        initial={editingAnnotation}
        onSave={handleDialogSave}
        onClose={() => {
          setDialogOpen(false); setEditingAnnotation(null);
          if (!editingAnnotation) { setPlacing(false); setPendingLatLng(null); }
        }}
      />
    </Box>
  );
}
